import { useCallback, useEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import type { ISpaceAuthLookupResponse } from "@/api/space/dtos/authenticator.dto";
import { SPACE_BYPASS_2FA } from "@/configs/runtime";
import { enqueueUiError } from "@/utils/errors/uiErrorMessage";
import { encontrarCorrespondenciaExataUsuario } from "../../../../utils/selecaoUsuarioJwt";
import { deveEscolherEntreMultiplos, deveUsarFluxoOtpDireto } from "../utils/loginGrupoFixo.utils";
import type { IntencaoBuscaUsuario, SolicitacaoBuscaUsuario } from "../loginGrupoFixo.types";
import useBuscaUsuarioLogin from "./useBuscaUsuarioLogin";

// --------------------------------- TYPES ---------------------------------
type UseSelecaoUsuarioLoginGrupoFixoOptions = {
	readonly username: string;
	readonly groupId: string;
	readonly aoAlterarUsuario: (nome: string) => void;
	readonly aoSelecionar: (usuario: ISpaceAuthLookupResponse, intencao: IntencaoBuscaUsuario) => void;
	readonly aoEntrar: () => void;
};

// ------------------------------- FUNCTIONS -------------------------------
export default function useSelecaoUsuarioLoginGrupoFixo({
	username,
	groupId,
	aoAlterarUsuario,
	aoSelecionar,
	aoEntrar,
}: Readonly<UseSelecaoUsuarioLoginGrupoFixoOptions>) {
	// -------------------------------- STATES --------------------------------
	const [selectedUser, setSelectedUser] = useState<ISpaceAuthLookupResponse | null>(null);
	const [dialogAberto, setDialogAberto] = useState(false);
	const [solicitacao, setSolicitacao] = useState<SolicitacaoBuscaUsuario | null>(null);
	const [intencaoDialog, setIntencaoDialog] = useState<IntencaoBuscaUsuario>("selecionar");
	const selectedUserRef = useRef(selectedUser);
	selectedUserRef.current = selectedUser;
	const ultimaFalhaNotificadaRef = useRef("");
	const erroAnteriorRef = useRef(false);

	// -------------------------------- QUERIES -------------------------------
	const busca = useBuscaUsuarioLogin({ username, selectedUser, groupId, enabled: groupId.length > 0 });
	const lookupEnabled = busca.lookupByIdEnabled || busca.lookupByNameEnabled;

	// ----------------------------- DERIVED STATE ----------------------------
	const mustChoose = deveEscolherEntreMultiplos(username, busca.options, selectedUser);
	const isDirectOtpFlow = deveUsarFluxoOtpDireto(selectedUser, SPACE_BYPASS_2FA);

	// -------------------------------- HANDLERS ------------------------------
	const selecionar = useCallback(
		(usuario: ISpaceAuthLookupResponse, intencao: IntencaoBuscaUsuario = "selecionar") => {
			selectedUserRef.current = usuario;
			setSelectedUser(usuario);
			setDialogAberto(false);
			setIntencaoDialog("selecionar");
			aoAlterarUsuario(String(usuario.nome ?? usuario.id_cad_pessoa ?? ""));
			aoSelecionar(usuario, intencao);
		},
		[aoAlterarUsuario, aoSelecionar],
	);

	function alterarUsuario(value: string): void {
		const nome = value.toUpperCase();
		aoAlterarUsuario(nome);
		setSolicitacao(null);
		const atual = selectedUserRef.current;
		if (!atual) return;
		if (atual.nome?.trim().toUpperCase() === nome.trim() || String(atual.id_cad_pessoa) === nome.trim()) return;
		selectedUserRef.current = null;
		setSelectedUser(null);
	}

	function fecharDialog(): void {
		setDialogAberto(false);
		setIntencaoDialog("selecionar");
	}

	function tratarTecla(evento: KeyboardEvent): void {
		if (evento.key === "Enter" && deveUsarFluxoOtpDireto(selectedUserRef.current, SPACE_BYPASS_2FA)) {
			evento.preventDefault();
			aoEntrar();
			return;
		}
		if ((evento.key !== "Tab" && evento.key !== "Enter") || !lookupEnabled) return;
		if (evento.key === "Enter") evento.preventDefault();
		setSolicitacao({ termo: username.trim(), intencao: evento.key === "Enter" ? "entrar" : "selecionar" });
	}

	// -------------------------------- EFFECTS -------------------------------
	useEffect(() => {
		const texto = busca.debouncedUsername.trim();
		if (
			busca.isError &&
			!erroAnteriorRef.current &&
			lookupEnabled &&
			texto &&
			busca.errorReason?.message === "Failed to fetch" &&
			ultimaFalhaNotificadaRef.current !== texto
		) {
			ultimaFalhaNotificadaRef.current = texto;
			enqueueUiError({ message: "Verifique sua conexão com a internet e tente novamente." });
		}
		if (!busca.isError) ultimaFalhaNotificadaRef.current = "";
		erroAnteriorRef.current = busca.isError;
	}, [busca.isError, busca.debouncedUsername, busca.errorReason, lookupEnabled]);

	useEffect(() => {
		if (!solicitacao || busca.isLoading) return;
		setSolicitacao(null);

		if (solicitacao.termo !== username.trim() || !lookupEnabled) return;

		const exato = busca.isError ? null : encontrarCorrespondenciaExataUsuario(username, busca.options);
		if (exato) return selecionar(exato, solicitacao.intencao);

		setIntencaoDialog(solicitacao.intencao);
		setDialogAberto(true);
	}, [solicitacao, busca.isLoading, busca.isError, busca.options, username, lookupEnabled, selecionar]);

	// -------------------------------- RETURN --------------------------------
	return {
		options: busca.options,
		loadingUsers: busca.isLoading,
		lookupError: busca.isError,
		lookupEnabled,
		mustChoose,
		isDirectOtpFlow,
		dialogAberto,
		obterUsuarioSelecionado: () => selectedUserRef.current,
		alterarUsuario,
		tratarTecla,
		fecharDialog,
		selecionarNoDialog: (usuario: ISpaceAuthLookupResponse) => selecionar(usuario, intencaoDialog),
	};
}
